import React, {memo, useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import GoogleMaps, {Marker, InfoWindow} from '@app/components/google-maps'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import StoreInfowindow from './store-infowindow'

import useShipToCountry from '../../hooks/use-ship-to-country'
import {getCountryNameByCode} from '../../utils/ship-to-country-utils'

/**
 * StoreMap is a memoized React component that renders a Google Map displaying store locations and the user's current position.
 *
 * The map automatically centers and adjusts its bounds to include the user's location and all nearby stores within a 500km radius. Store markers are displayed using custom icons based on store type, and clicking a marker reveals an InfoWindow with additional store details. The component manages active marker state to control which InfoWindow is open, and recenters the map when the user's location changes.
 *
 * Props:
 * @param {boolean} isApiLoaded - Indicates whether the Google Maps API has finished loading.
 * @param {Array<Object>} filteredStores - The list of store objects to display as markers on the map.
 * @param {Object} location - The user's current location.
 * @param {number} location.latitude - The latitude of the user's location.
 * @param {number} location.longitude - The longitude of the user's location.
 * @param {Array<Object>} stores - The complete list of stores (used for recalculating bounds).
 *
 * Features:
 * - Dynamically fits the map view to include the user's location and all nearby stores.
 * - Displays custom marker icons based on store type.
 * - Shows an InfoWindow with store details when a marker is clicked.
 * - Recenters and resets active marker state when the user's location changes.
 *
 * Dependencies:
 * - Google Maps JavaScript API
 * - React, React.memo, useState, useEffect, useRef, useCallback
 * - GoogleMaps, Marker, InfoWindow, and StoreInfowindow components
 */
const StoreMap = memo(
    ({isApiLoaded, filteredStores, location, stores, isDeliveryCountryBonds = true}) => {
        const mapRef = useRef(null)
        const {shipToCountry} = useShipToCountry()
        const [activeMarker, setActiveMarker] = useState(null)
        const [lastPosition, setLastPosition] = useState({
            lat: location.latitude,
            lng: location.longitude
        })
        const containerStyle = {
            width: '100%',
            height: '100%'
        }

        /**
         * Effect hook that adjusts the map view to fit the user's location and nearby stores within a 500km radius.
         * It updates the map bounds based on the user's current location and the filtered store markers.
         *
         * @param {Object} location - The current location of the user.
         * @param {number} location.latitude - The latitude of the user's location.
         * @param {number} location.longitude - The longitude of the user's location.
         * @param {Array} stores - The list of stores to be filtered and displayed on the map.
         */
        useEffect(() => {
            setLastPosition({
                lat: location.latitude,
                lng: location.longitude
            })
            adjustMapBoundaries()
        }, [location, stores])

        const getBoundsByCountryCode = async (countryCode) => {
            if (!isApiLoaded) {
                throw new Error('Google Maps API is not loaded.')
            }

            // https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderRequest
            const boundCountryName = getCountryNameByCode(countryCode)
            if (!boundCountryName) {
                throw new Error(`Country name not found for the given code: ${countryCode}`)
            }

            return new Promise((resolve, reject) => {
                const geocoder = new window.google.maps.Geocoder()

                geocoder.geocode({address: boundCountryName}, (results, status) => {
                    if (status === window.google.maps.GeocoderStatus.OK) {
                        let countryBounds
                        if (results[0].geometry.bounds) {
                            countryBounds = results[0].geometry.bounds
                        } else {
                            countryBounds = new window.google.maps.LatLngBounds()
                            countryBounds.extend(results[0].geometry.location)
                        }
                        resolve(countryBounds)
                    } else {
                        reject(`Geocode was not successful for the following reason: ${status}`)
                    }
                })
            })
        }

        /**
         * Adjusts the Google Map view to fit the user's current location and nearby area within a 500km radius
         * or area of stores of delivery country with nearby area within a 25km radius on checkout.
         *
         * This function calculates the bounds based on the user's latitude and longitude, and extends
         * the bounds to include a circle with a 500km radius around the user's location. It then fits
         * the map to these bounds.
         *
         * @function
         * @returns {void}
         */
        const adjustMapBoundaries = async () => {
            if (mapRef.current && location.latitude && location.longitude) {
                const bounds = new window.google.maps.LatLngBounds()

                // map boundaries set to delivery country and all checkout stores
                // use case: checkout map modal
                if (isDeliveryCountryBonds) {
                    // Extend bounds by country
                    try {
                        const countryBounds = await getBoundsByCountryCode(shipToCountry?.id)
                        bounds.union(countryBounds)
                    } catch (error) {
                        console.error('Error adjusting map boundaries:', error)
                    }

                    // Bulletproof logic: extend bounds by all stores of delivery country
                    filteredStores.forEach((store) => {
                        const lat = parseFloat(store.c_vbqLatitude || store.latitude, 10)
                        const lng = parseFloat(store.c_vbqLongitude || store.longitude, 10)
                        const storeLocation = new window.google.maps.LatLng(lat, lng)

                        const circle = new window.google.maps.Circle({
                            center: storeLocation,
                            radius: 25 * 1000 // 25km in meters
                        })

                        bounds.union(circle.getBounds())
                    })

                    mapRef.current.fitBounds(bounds)
                } else {
                    // default: adjust map to user location
                    // use case: store locator page
                    const userLocation = new window.google.maps.LatLng(
                        location.latitude,
                        location.longitude
                    )

                    // Add user location to bounds
                    bounds.extend(userLocation)

                    const circle = new window.google.maps.Circle({
                        center: userLocation,
                        radius: 500 * 1000 // 500km in meters
                    })

                    // Extend bounds to include the circle
                    bounds.union(circle.getBounds())

                    mapRef.current.fitBounds(bounds)
                }
            }
        }

        const handleMapLoad = (map) => {
            mapRef.current = map
            adjustMapBoundaries()
        }

        const handleActiveMarker = useCallback(
            (marker, lat = false, lng = false) => {
                if (lat && lng) {
                    setLastPosition({
                        lat,
                        lng
                    })
                }
                if (marker === activeMarker) {
                    return
                }
                setActiveMarker(marker)
            },
            [activeMarker]
        )

        useEffect(() => {
            setActiveMarker(null)
        }, [location])

        return (
            <GoogleMaps
                id="stores-google-map"
                isApiLoaded={isApiLoaded}
                mapContainerStyle={containerStyle}
                // center on user location only not on checkout with stores of delivery country
                {...(!isDeliveryCountryBonds && {
                    center: {
                        lat: lastPosition.lat || 0,
                        lng: lastPosition.lng || 0
                    }
                })} // Center on user location
                onLoad={handleMapLoad}
            >
                {filteredStores?.map((marker) => {
                    // Create an image element for the marker icon
                    const markerImg = document.createElement('img')
                    markerImg.src = getAssetUrl(
                        `static/img/${marker.c_storeType?.toLowerCase() || 'os'}.png`
                    )

                    const lat = parseFloat(marker.c_vbqLatitude || marker.latitude, 10)
                    const lng = parseFloat(marker.c_vbqLongitude || marker.longitude, 10)

                    return (
                        <Marker
                            key={marker.id}
                            position={{
                                lat,
                                lng
                            }}
                            icon={markerImg}
                            onClick={() => handleActiveMarker(marker.id, lat, lng)}
                            title={marker.city}
                        >
                            {activeMarker === marker.id ? (
                                <InfoWindow
                                    position={{
                                        lat,
                                        lng
                                    }}
                                    onCloseClick={() => handleActiveMarker(null)}
                                >
                                    <StoreInfowindow store={marker} />
                                </InfoWindow>
                            ) : null}
                        </Marker>
                    )
                })}
            </GoogleMaps>
        )
    }
)

StoreMap.displayName = 'StoreMap'

StoreMap.propTypes = {
    isApiLoaded: PropTypes.bool.isRequired,
    filteredStores: PropTypes.array.isRequired,
    location: PropTypes.object,
    stores: PropTypes.object.isRequired,
    isDeliveryCountryBonds: PropTypes.bool
}

export default StoreMap
